import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, X, Minimize2, Maximize2, UserRound, Languages } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const MODES = [
  ["improve", Sparkles, "aiImprove"], ["shorter", Minimize2, "aiShorter"], ["longer", Maximize2, "aiLonger"], ["customer", UserRound, "aiCustomer"],
  ["translate_en", Languages, "aiToEn"], ["translate_ar", Languages, "aiToAr"], ["translate_nl", Languages, "aiToNl"],
];

/* ✨ AI-knop naast een tekstveld: stelt verbeterde tekst voor; gebruiker bevestigt. value/onApply; field bepaalt de schrijfstijl; context = voertuig/klussen (geen prijzen). */
export function AiTextButton({ value, onApply, field = "notes", context, disabled, size = "sm", testId = "ai-text" }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [proposal, setProposal] = useState(null);
  const run = async (mode) => {
    if (!value?.trim()) return toast.error(t("aiNeedText"));
    setBusy(mode);
    try { const { data } = await api.post("/ai/improve-text", { text: value, mode, field, context }); setProposal(data.text); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  const apply = () => { onApply(proposal); setProposal(null); setOpen(false); toast.success(t("aiApplied")); };
  const iconCls = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setProposal(null); }}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} title={t("aiTitle")} className={`inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 text-primary hover:bg-primary/15 transition-colors disabled:opacity-40 ${size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-3 py-1 text-xs"}`} data-testid={`${testId}-button`}>
          <Sparkles className={iconCls} /> AI
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-3 space-y-2" data-testid={`${testId}-popover`}>
        <div className="text-xs font-medium flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-primary" />{t("aiTitle")}</div>
        <div className="flex flex-wrap gap-1.5">
          {MODES.map(([m, Icon, k]) => (
            <button key={m} type="button" disabled={!!busy} onClick={() => run(m)} className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] hover:bg-muted disabled:opacity-50" data-testid={`${testId}-mode-${m}`}>
              {busy === m ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}{t(k)}
            </button>
          ))}
        </div>
        {proposal != null && (
          <div className="space-y-2">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-2 text-sm whitespace-pre-wrap max-h-56 overflow-auto" dir="auto" data-testid={`${testId}-proposal`}>{proposal}</div>
            <div className="flex justify-end gap-1.5">
              <button type="button" onClick={() => setProposal(null)} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs hover:bg-muted" data-testid={`${testId}-reject`}><X className="h-3 w-3" />{t("aiReject")}</button>
              <button type="button" onClick={apply} className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 py-1 text-xs hover:opacity-90" data-testid={`${testId}-apply`}><Check className="h-3 w-3" />{t("aiApply")}</button>
            </div>
          </div>
        )}
        <p className="text-[10px] text-muted-foreground">{t("aiHint")}</p>
      </PopoverContent>
    </Popover>
  );
}

export const repairAiContext = (card) => ({
  vehicle: [card?.car_make, card?.car_model].filter(Boolean).join(" "), plate: card?.car_plate,
  services: (card?.services || []).map((s) => s.name), parts: [...(card?.parts_used || []), ...(card?.special_parts || [])].map((p) => p.name),
});
